import React, { useState, useEffect } from 'react';
import { Position } from './PositionCard';

interface Props {
  position: Position;
  onConfirm: (slPrice: number) => Promise<void>;
  onCancel: () => void;
}

function fmtPrice(n: number | null) {
  if (n === null) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

function platformLabel(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'Hyperliquid';
  if (exchange === 'BINANCE') return 'Binance';
  return 'Bitget';
}
function platformClass(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'hl';
  if (exchange === 'BINANCE') return 'bn';
  return 'bg';
}

const EditSlModal: React.FC<Props> = ({ position: pos, onConfirm, onCancel }) => {
  const [slInput, setSlInput] = useState(pos.sl_price ? String(pos.sl_price) : '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const slVal = parseFloat(slInput);
  const entry = pos.entry_price;
  const current = pos.current_price;
  const qty = pos.remaining_quantity;

  const distFromEntry = !isNaN(slVal) && entry ? slVal - entry : null;
  const distPct = distFromEntry !== null && entry ? (distFromEntry / entry * 100) : null;
  const maxLoss = distFromEntry !== null ? distFromEntry * qty : null;
  const distFromCurrent = !isNaN(slVal) && current ? current - slVal : null;

  const handleConfirm = async () => {
    if (isNaN(slVal) || slVal <= 0) {
      setError('请输入有效的止损价格');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(slVal);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || '修改失败');
      setLoading(false);
    }
  };

  return (
    <div className="pos-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="pos-modal">
        <div className="pos-modal-head">
          <div className="pos-modal-title">
            <span className={`pos-platform ${platformClass(pos.exchange)}`}>
              <span className="pos-platform-dot" />
              {platformLabel(pos.exchange)}
            </span>
            Edit Stop Loss
          </div>
          <button className="pos-modal-x" onClick={onCancel}>×</button>
        </div>

        <div className="pos-modal-body">
          {error && <div className="pos-error" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="pos-sum-row">
            <span className="pos-sum-label">Position</span>
            <span className="pos-sum-val">
              {pos.symbol}-PERP · <span style={{ color: pos.direction === 'LONG' ? '#16a34a' : '#dc2626' }}>{pos.direction}</span>
            </span>
          </div>
          <div className="pos-sum-row">
            <span className="pos-sum-label">Entry Price</span>
            <span className="pos-sum-val">{fmtPrice(pos.entry_price)}</span>
          </div>
          <div className="pos-sum-row" style={{ marginBottom: 16 }}>
            <span className="pos-sum-label">Current Stop Loss</span>
            <span className="pos-sum-val" style={{ color: pos.sl_price ? '#dc2626' : '#9ca3af' }}>
              {pos.sl_price ? fmtPrice(pos.sl_price) : '—'}
            </span>
          </div>

          <div className="pos-form-group">
            <div className="pos-form-label">
              New Stop Loss Price
              <span className="pos-form-hint">Current: {fmtPrice(current)}</span>
            </div>
            <input
              className="pos-form-input"
              type="number"
              step="any"
              placeholder="e.g. 108.50"
              value={slInput}
              onChange={e => setSlInput(e.target.value)}
              autoFocus
            />
          </div>

          {!isNaN(slVal) && slVal > 0 && (
            <div className="pos-risk-box">
              <div className="pos-risk-row">
                <span>Distance from entry</span>
                <span className="pos-risk-val">
                  {distFromEntry !== null
                    ? `${distFromEntry >= 0 ? '+' : ''}$${distFromEntry.toFixed(2)} (${distPct?.toFixed(1)}%)`
                    : '—'}
                </span>
              </div>
              <div className="pos-risk-row">
                <span>Max loss if triggered</span>
                <span className={`pos-risk-val ${maxLoss !== null && maxLoss < 0 ? 'red' : ''}`}>
                  {maxLoss !== null
                    ? `${maxLoss >= 0 ? '+' : ''}$${maxLoss.toFixed(2)}`
                    : '—'}
                </span>
              </div>
              <div className="pos-risk-row">
                <span>Distance from current</span>
                <span className="pos-risk-val">
                  {distFromCurrent !== null
                    ? `$${Math.abs(distFromCurrent).toFixed(2)} ${slVal < (current || 0) ? 'below' : 'above'} current`
                    : '—'}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="pos-modal-foot">
          <button className="pos-btn pos-btn-outline" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="pos-btn pos-btn-primary" onClick={handleConfirm} disabled={loading}>
            {loading ? <><span className="pos-spinner" />Updating...</> : 'Confirm Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSlModal;
